import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Popup from "src/components/Popup/Popup";
import type { RunResults } from "src/context/PraxisSoftwareSyncContext";
import "./praxisSoftwareSyncResultsPopup.scss";

/** Shown after a sync run that actually processed (or failed to process) something —
 *  deliberately mirrors PadUploadPopup.tsx's results summary (same stat layout, same
 *  wording) for the .dat/PADneXt side, per the practice's request for "the same popup
 *  as with the pad upload". .gdt outcomes are NOT part of this popup — they depend on an
 *  async staff decision (GdtPatientReviewPopup) that can happen well after this
 *  synchronous run completes, so they're only ever recorded in the Activity log. */
export function PraxisSoftwareSyncResultsPopup({ results, onClose }: { results: RunResults; onClose: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { dat, failures } = results;

    return (
        <Popup
            title={t("praxisSoftwareSync.resultsTitle")}
            onClose={onClose}
            message={
                <div className="pvs-results">
                    {dat && (
                        <div className="pvs-results-section">
                            <div className="pvs-results-summary">
                                {dat.duplicateUpload && (
                                    <p className="pvs-results-summary__duplicate-warning">
                                        {t(
                                            dat.duplicateUpload.sameDoctor
                                                ? "praxisSoftwareSync.duplicateUploadSame"
                                                : "praxisSoftwareSync.duplicateUploadOther",
                                            {
                                                name: dat.duplicateUpload.uploadedByName ?? t("praxisSoftwareSync.duplicateUploadUnknownDoctor"),
                                                date: new Date(dat.duplicateUpload.uploadedAt).toLocaleDateString("de-DE"),
                                            },
                                        )}
                                    </p>
                                )}
                                <div className="pvs-results-summary__stat">
                                    <span className="pvs-results-summary__value">{dat.invoices}</span>
                                    <span className="pvs-results-summary__label">{t("praxisSoftwareSync.summaryInvoices")}</span>
                                </div>
                                <div className="pvs-results-summary__stat">
                                    <span className="pvs-results-summary__value">{dat.recipients}</span>
                                    <span className="pvs-results-summary__label">{t("praxisSoftwareSync.summaryAccountPatients")}</span>
                                </div>
                                <div className="pvs-results-summary__stat">
                                    <span className="pvs-results-summary__value">{dat.patients}</span>
                                    <span className="pvs-results-summary__label">{t("praxisSoftwareSync.summaryLinkedPatients")}</span>
                                </div>
                                <div className="pvs-results-summary__stat pvs-results-summary__stat--amount">
                                    <span className="pvs-results-summary__value">
                                        €{dat.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                    <span className="pvs-results-summary__label">{t("praxisSoftwareSync.summaryTotal")}</span>
                                </div>
                                {dat.samePersonInvoices > 0 && (
                                    <p className="pvs-results-summary__same-person">
                                        {t("praxisSoftwareSync.summarySamePerson", { count: dat.samePersonInvoices })}
                                    </p>
                                )}
                                {(dat.emailsSent > 0 || dat.emailsFailed > 0) && (
                                    <div className="pvs-results-summary__email-row">
                                        {dat.emailsSent > 0 && (
                                            <span className="pvs-results-summary__email-chip pvs-results-summary__email-chip--sent">
                                                {t("praxisSoftwareSync.emailsSent", { count: dat.emailsSent })}
                                            </span>
                                        )}
                                        {dat.emailsFailed > 0 && (
                                            <span className="pvs-results-summary__email-chip pvs-results-summary__email-chip--failed">
                                                {t("praxisSoftwareSync.emailsFailed", { count: dat.emailsFailed })}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {dat.sendFailures.length > 0 && (
                                <div className="pvs-results-failures">
                                    <div className="pvs-results-failures__title">{t("praxisSoftwareSync.sendFailuresTitle")}</div>
                                    <ul className="pvs-results-failures__list">
                                        {dat.sendFailures.map((f, i) => (
                                            <li key={`${f.readableId}-${i}`} className="pvs-results-failures__item">
                                                <span className="pvs-results-failures__id">{f.readableId}</span>
                                                {f.recipientName && <span className="pvs-results-failures__name">{f.recipientName}</span>}
                                                <span className="pvs-results-failures__reason">
                                                    {t(`praxisSoftwareSync.sendFailureReason${f.reason}`, { defaultValue: t("praxisSoftwareSync.sendFailureReasonUnknown") })}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {dat.recipientsMissingEmail > 0 && (
                                <div className="pvs-results-nudge">
                                    <p className="pvs-results-nudge__text">{t("praxisSoftwareSync.whatsNextMissingEmailBody")}</p>
                                    <button
                                        type="button"
                                        className="pvs-results-nudge__cta"
                                        onClick={() => { onClose(); navigate("/patients"); }}
                                    >
                                        {t("praxisSoftwareSync.whatsNextPatientsCta")}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {failures.length > 0 && (
                        <div className="pvs-results-section">
                            <div className="pvs-results-failures">
                                <div className="pvs-results-failures__title">{t("praxisSoftwareSync.filesFailedTitle")}</div>
                                <ul className="pvs-results-failures__list">
                                    {failures.map((f, i) => (
                                        <li key={`${f.filename}-${i}`} className="pvs-results-failures__item">
                                            <span className="pvs-results-failures__id">{f.filename}</span>
                                            <span className="pvs-results-failures__reason">{f.message}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            }
            actions={[
                { label: t("popup.ok"), variant: "primary", onClick: onClose },
            ]}
        />
    );
}
